import { booleanColumn, floatColumn, moneyColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import i18n from "i18next";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { FormCrudDataGrid } from "src/components/common/dataGrid/crud/FormCrudDataGrid.tsx";
import { faCopy } from "@fortawesome/pro-regular-svg-icons";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { RefreshableElementProps, RefreshableElementRef } from "src/utils/useRefreshRef.ts";
import { CustomerOfferBillingPlanApi } from "src/api/generated/erp/sales/billingPlan/customerOffer/api/customerOfferBillingPlanApi.ts";
import { BillingPlanSummaryText } from "src/components/views/erp/sales/billingPlan/base/BillingPlanSummaryText.tsx";
import { CustomerOfferBillingPlanLineView } from "src/api/generated/erp/db/types/tables/customerOfferBillingPlanLineView.ts";
import { CustomerOfferBillingPlanLineForm } from "src/components/views/erp/sales/billingPlan/customerOffer/CustomerOfferBillingPlanLineForm.tsx";
import { CustomerOfferBillingPlanEditApi } from "src/api/generated/erp/sales/billingPlan/customerOffer/api/customerOfferBillingPlanEditApi.ts";
import { useInputDialog } from "src/components/common/dialogs/input/useInputDialog.tsx";
import { BillingPlanModelApi } from "src/api/generated/erp/sales/billingPlan/model/api/billingPlanModelApi";
import { OpenObjectChangeLogButton } from "src/components/views/changeLogging/OpenObjectChangeLogButton.tsx";
import { formatLabelWithCurrencyCode, renderMoneyCellWithCurrency } from "src/components/views/erp/sales/salesUtils.ts";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";

export interface CustomerOfferBillingPlanDataGridProps extends RefreshableElementProps {
	customerOfferId: number;
	onSelectionChanged?: (selectedRows: CustomerOfferBillingPlanLineView[]) => void;
	customerOrderOffersRefreshRef: RefreshableElementRef;
}

export const CustomerOfferBillingPlanDataGrid = ({
	customerOfferId,
	refreshRef,
	onSelectionChanged,
	customerOrderOffersRefreshRef,
}: CustomerOfferBillingPlanDataGridProps) => {
	const showInputDialog = useInputDialog();

	return (
		<ClientSideDataGridModel
			fetchData={() =>
				CustomerOfferBillingPlanApi.getCustomerOfferBillingPlan({
					customerOfferId,
				})
			}
			refreshRef={refreshRef}
			getRows={(data) => data.lines}
			initialParams={{}}
			gridId={"E9E5FA39C483F3C5"}
			getRowId={(row) => row.customerOfferBillingPlanLineId}
			customFunctionUiActionView={"CUSTOMER_OFFER_BILLING_PLAN_LINES"}
			onSelectionChanged={onSelectionChanged}
			render={({ data: { customerOffer, summary, lines, currencyCode }, dataGridProps, refreshData }) => {
				return (
					<FormCrudDataGrid<CustomerOfferBillingPlanLineView>
						disableMultipleRowSelection
						listViewOnMobile
						columns={[
							textColumn({
								field: "description",
								headerName: i18n.t("description"),
								width: 200,
								mobile: "header",
							}),
							moneyColumn({
								field: "billingPlanSum",
								headerName: formatLabelWithCurrencyCode(i18n.t("sum"), currencyCode),
								mobile: "cell",
								renderCell: renderMoneyCellWithCurrency(currencyCode),
							}),
							moneyColumn({
								field: "billingPlanSumWithVat",
								headerName: formatLabelWithCurrencyCode(i18n.t("sum_with_vat"), currencyCode),
								width: 140,
								mobile: "cell",
								renderCell: renderMoneyCellWithCurrency(currencyCode),
							}),
							booleanColumn({
								field: "funded",
								headerName: i18n.t("funded"),
								mobile: "cell",
							}),
							textColumn({
								field: "billingPlanPartName",
								headerName: i18n.t("billing_product"),
								width: 150,
							}),
							floatColumn({
								field: "vatPercent",
								headerName: i18n.t("vat_percent"),
								mobile: "cell",
							}),
						]}
						form={{
							addRowEnabled: !customerOffer.isClosed,
							editEnabled: !customerOffer.isClosed,
							dialogSize: "md",
							dialogTitle: i18n.t("billing_plan_line"),
							agentContext: (row) => ({
								key: defineAgentUiContextKey(
									"erp.sales.billingPlan.customerOfferBillingPlanLine.formDialog",
								),
								data: {
									customerOfferId,
									customerOfferBillingPlanLineId: row?.customerOfferBillingPlanLineId ?? null,
								},
								pathSegments:
									row?.customerOfferBillingPlanLineId ?
										[`${i18n.t("billing_plan_line")} ${row.customerOfferBillingPlanLineId}`]
									:	[i18n.t("billing_plan_line")],
							}),
							component: ({ row, onCompleted, onFormEdited }) => (
								<CustomerOfferBillingPlanLineForm
									customerOfferId={customerOfferId}
									billingPlanLine={row}
									currencyCode={currencyCode}
									onFormEdited={onFormEdited}
									onCompleted={async (result) => {
										onCompleted(result);
										if (result.type === "success") {
											await customerOrderOffersRefreshRef.refresh();
										}
									}}
								/>
							),
						}}
						actionBarComponents={
							<>
								<AsyncButton
									label={i18n.t("create_from_template")}
									icon={faCopy}
									variant={"outlined"}
									disabled={lines.length > 0}
									onClick={createLineFromModel}
								/>
								<BillingPlanSummaryText summary={summary} />
							</>
						}
						rowContextMenuComponents={({ row }) => [
							<OpenObjectChangeLogButton
								key={"changeLog"}
								objectRef={{
									objectType: "CUSTOMER_OFFER_BILLING_PLAN_LINE",
									objectId: row.customerOfferBillingPlanLineId,
								}}
							/>,
						]}
						remove={
							!customerOffer.isClosed &&
							(({ items }) =>
								CustomerOfferBillingPlanEditApi.delete_({
									billingPlanLineIds: items.map((i) => i.customerOfferBillingPlanLineId),
								}))
						}
						agentContext={{
							key: defineAgentUiContextKey("erp.sales.billingPlan.customerOfferBillingPlan.grid"),
							data: { customerOfferId },
							selectedRowPathSegment: (row) =>
								`${i18n.t("billing_plan_line")} ${row.customerOfferBillingPlanLineId}`,
							rowFields: [
								"customerOfferBillingPlanLineId",
								"customerOfferId",
								"billingPlanPartId",
								"description",
							],
						}}
						{...dataGridProps}
					/>
				);

				async function createLineFromModel() {
					const modelOptions = await BillingPlanModelApi.getBillingPlanModelOptionsForCustomerOffer();

					const billingPlanModelId = await showInputDialog({
						type: "singleNumberSelect",
						title: i18n.t("create_from_template"),
						fieldLabel: i18n.t("template"),
						required: true,
						defaultValue: null,
						fieldProps: {
							selection: {
								options: modelOptions.map((option) => ({
									value: option.billingPlanModelId,
									label: option.name,
								})),
							},
						},
					});
					if (billingPlanModelId === undefined) return;

					await BillingPlanModelApi.copyBillingPlanModelToCustomerOffer({
						customerOfferId,
						billingPlanModelId,
					});

					await Promise.all([refreshData(), customerOrderOffersRefreshRef.refresh()]);
				}
			}}
		/>
	);
};
